"use client";

import { useCallback, useRef } from "react";
import useTranslations from "@/i18n/useTranslations";
import { locales, type Locale } from "@/i18n/config";
import { cn } from "@/lib/utils";
import { useLocaleSwitch } from "@/hooks/useLocaleSwitch";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementType } from "@/constants/tagular/main";
import { createCustomDimensions } from "@/utils/tagular/createDimension";

type LocalePillToggleProps = {
  className?: string;
  location?: string;
  "data-testid"?: string;
};

export function LocalePillToggle({
  className,
  location = "header",
  "data-testid": dataTestId,
}: LocalePillToggleProps) {
  const groupRef = useRef<HTMLDivElement>(null);
  const t = useTranslations("LocaleSwitcher");
  const { locale, isPending, isHidden, switchLocale } = useLocaleSwitch();
  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: "language-toggle",
      location,
      name: "language-toggle",
    },
  });

  const selectLocale = (optionLocale: Locale) => {
    if (isPending || locale === optionLocale) {
      return;
    }

    elementClicked({
      webElement: { text: optionLocale.toUpperCase() },
      customDimensions: createCustomDimensions([
        ["switch_direction", `${locale}-to-${optionLocale}`],
        ["from_locale", locale],
        ["to_locale", optionLocale],
        ["source_path", window.location.pathname],
      ]),
    });
    switchLocale(optionLocale);
  };

  const handleGroupKeyDown = useCallback((event: React.KeyboardEvent) => {
    const items =
      groupRef.current?.querySelectorAll<HTMLElement>('[role="radio"]');
    if (!items?.length) {
      return;
    }

    const currentIndex = Array.from(items).findIndex(
      (item) => item === document.activeElement,
    );
    let nextIndex: number | null = null;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      nextIndex = (currentIndex + 1) % items.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      nextIndex = (currentIndex - 1 + items.length) % items.length;
    }

    if (nextIndex != null) {
      items[nextIndex].focus();
    }
  }, []);

  const handleOptionKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    optionLocale: Locale,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectLocale(optionLocale);
    }
  };

  if (isHidden) {
    return null;
  }

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label={t("label")}
      data-testid={dataTestId}
      className={cn(
        "inline-flex overflow-hidden rounded-[8px] border border-putty-400",
        className,
      )}
      onKeyDown={handleGroupKeyDown}
    >
      {locales.map((optionLocale: Locale) => {
        const isSelected = locale === optionLocale;

        return (
          <button
            key={optionLocale}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={t(optionLocale)}
            disabled={isPending}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => selectLocale(optionLocale)}
            onKeyDown={(event) => handleOptionKeyDown(event, optionLocale)}
            className={cn(
              "min-w-11 px-4 py-1.5 text-sm font-medium transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500",
              isSelected
                ? "bg-primary text-primary-foreground"
                : "bg-putty-200 text-primary",
            )}
          >
            {optionLocale.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}
